import axios from 'axios';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

const baseURL = process.env.REACT_APP_API_URL;

const axiosInstance = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
  },
});

export const paymentMethodsAPI = {
  getAll: () => axiosInstance.get('/PaymentMethods/'),
  getById: (id) => axiosInstance.get(`/PaymentMethods/${id}/`),
  delete: (id) => axiosInstance.delete(`/PaymentMethods/${id}/Delete/`),

  update: (id, data) => {
    return axios.put(`${baseURL}/PaymentMethods/${id}/Update/`, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
      },
    });
  },
  create: (data) => {
    return axios.post(`${baseURL}/PaymentMethods/Create/`, data, {
      headers: {
        'Content-Type': 'multipart/form-data', // Set correct content type for file upload
        Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
      },
    });
  },
};

const initialState = {
  items: [],
  selectedMethod: null,
  status: 'idle',
  error: null,
  updateStatus: 'idle',
  deleteStatus: 'idle',
  createStatus: 'idle',
};

const handleApiResponse = (response, successMessage) => {
  if (response?.data?.message) {
    toast.success(response.data.message);
  } else if (successMessage) {
    toast.success(successMessage);
  }
  return response.data;
};

// Show error message from API or fallback
const handleApiError = (error, fallbackMessage) => {
  const errorMessage = error.response?.data?.message || fallbackMessage;
  toast.error(errorMessage);
  return error.response?.data || fallbackMessage;
};

export const fetchPaymentMethods = createAsyncThunk(
  'paymentMethods/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await paymentMethodsAPI.getAll();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Error fetching payment methods');
    }
  }
);

export const fetchPaymentMethodById = createAsyncThunk(
  'paymentMethods/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await paymentMethodsAPI.getById(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Error fetching payment method');
    }
  }
);


export const createPaymentMethod = createAsyncThunk(
  'paymentMethods/create',
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await paymentMethodsAPI.create(data);
      await dispatch(fetchPaymentMethods());
      toast.success('تم إضافة طريقة الدفع بنجاح');
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data || 'فشل في إضافة طريقة الدفع';
      return rejectWithValue(errorMessage);
    }
  }
);

export const updatePaymentMethod = createAsyncThunk(
  'paymentMethods/update',
  async ({ id, data }, { rejectWithValue, dispatch }) => {
    try {
      const response = await paymentMethodsAPI.update(id, data);
      await dispatch(fetchPaymentMethods());
      toast.success('تم تحديث طريقة الدفع بنجاح');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'فشل في تحديث طريقة الدفع');
    }
  }
);


export const deletePaymentMethod = createAsyncThunk(
  'paymentMethods/delete',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const response = await paymentMethodsAPI.delete(id);
      await dispatch(fetchPaymentMethods());
      return handleApiResponse(response, 'تم حذف طريقة الدفع بنجاح');
    } catch (error) {
      return rejectWithValue(handleApiError(error, 'فشل في حذف طريقة الدفع'));
    }
  }
);

const paymentMethodsSlice = createSlice({
  name: 'paymentMethods',
  initialState,
  reducers: {
    clearSelectedMethod: (state) => {
      state.selectedMethod = null;
    },
    setStatus: (state, action) => {
      state.status = action.payload;
    },
    clearError: (state) => {
      state.error = null;
      state.updateStatus = 'idle';
      state.deleteStatus = 'idle';
      state.createStatus = 'idle';
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all payment methods
      .addCase(fetchPaymentMethods.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchPaymentMethods.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = action.payload;
      })
      .addCase(fetchPaymentMethods.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      // Fetch payment method by ID
      .addCase(fetchPaymentMethodById.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchPaymentMethodById.fulfilled, (state, action) => {
        state.selectedMethod = action.payload;
      })
      .addCase(fetchPaymentMethodById.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Update payment method
      .addCase(updatePaymentMethod.pending, (state) => {
        state.updateStatus = 'loading';
        state.error = null;
      })
      .addCase(updatePaymentMethod.fulfilled, (state) => {
        state.updateStatus = 'succeeded';
      })
      .addCase(updatePaymentMethod.rejected, (state, action) => {
        state.updateStatus = 'failed';
        state.error = action.payload;
      })
      // Delete payment method
      .addCase(deletePaymentMethod.pending, (state) => {
        state.deleteStatus = 'loading';
        state.error = null;
      })
      .addCase(deletePaymentMethod.fulfilled, (state) => {
        state.deleteStatus = 'succeeded';
      })
      .addCase(deletePaymentMethod.rejected, (state, action) => {
        state.deleteStatus = 'failed';
        state.error = action.payload;
      })
      // Create payment method
      .addCase(createPaymentMethod.pending, (state) => {
        state.createStatus = 'loading';
        state.error = null;
      })
      .addCase(createPaymentMethod.fulfilled, (state) => {
        state.createStatus = 'succeeded';
      })
      .addCase(createPaymentMethod.rejected, (state, action) => {
        state.createStatus = 'failed';
        state.error = action.payload;
      });
  },
});

// Selectors
export const selectAllPaymentMethods = (state) => state.paymentMethods.items;
export const selectPaymentMethodById = (state) => state.paymentMethods.selectedMethod;
export const selectPaymentMethodsStatus = (state) => state.paymentMethods.status;
export const selectPaymentMethodsError = (state) => state.paymentMethods.error;
export const selectUpdateStatus = (state) => state.paymentMethods.updateStatus;
export const selectDeleteStatus = (state) => state.paymentMethods.deleteStatus;
export const selectCreateStatus = (state) => state.paymentMethods.createStatus;

export const { clearSelectedMethod, setStatus, clearError } = paymentMethodsSlice.actions;
export default paymentMethodsSlice.reducer;